import { Box, Tooltip } from "@mui/material";
import { VM_Colors } from "../Styles/VMStyles";
import MUITypography from "../Components/MUITypography";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import axios from "axios";
import { API_DISPLAY_INVOICE, API_GET_ADMIN_MEMEBRS } from "./URLConstants";
import StorageFactory from "./StorageFactory";
import { SESSION_USER_TYPE_KEY } from "./VMConstants";
dayjs.extend(utc);


export const isEmpty = (value) => {
  // Null or undefined
  if (value === null || value === undefined) return true;

  // String or array
  if (typeof value === "string" || Array.isArray(value)) {
    return value.length === 0;
  }

  // Object
  if (typeof value === "object") {
    return Object.keys(value).length === 0;
  }

  // Everything else (number, boolean, function) is NOT empty
  return false;
};

/** True when login response is SuperAdmin or subAdmin (admin panel access). */
export function isAdminPanelLogin(respData) {
  if (!respData) return false;
  if (respData.admin_type === "SuperAdmin" || respData.admin_type === "subAdmin") return true;
  if (respData.is_super_admin === true || respData.is_superadmin === true) return true;
  const user = respData.user;
  if (!user) return false;
  if (user.user_type === "SuperAdmin" || user.user_type === "subAdmin") return true;
  if (user.permissions?.super_admin === true) return true;
  return false;
}

/** @deprecated Use isAdminPanelLogin */
export function isSuperAdminLogin(respData) {
  return isAdminPanelLogin(respData);
}

/** Normalize permissions from login — array of role objects or `{ super_admin: true }`. */
export function normalizeLoginPermissions(permissions) {
  if (isEmpty(permissions)) return [];
  if (Array.isArray(permissions)) {
    return permissions
      .map((perm) => {
        if (typeof perm === "string") return perm;
        if (typeof perm === "object" && perm != null) {
          if (perm.permission_id != null && perm.permission_id !== "") {
            return String(perm.permission_id);
          }
          if (typeof perm.id === "string" && /^VM\d+$/i.test(perm.id)) {
            return perm.id;
          }
        }
        return null;
      })
      .filter(Boolean);
  }
  if (typeof permissions === "object" && permissions.super_admin === true) {
    return ["super_admin"];
  }
  return [];
}

/** Calendar day as YYYY-MM-DD, independent of ISO vs DD-MM-YYYY vs picker DateObject. */
export const toDateKey = (value) => {
  if (!value && value !== 0) return "";

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return "";
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  if (typeof value?.format === "function") {
    return toDateKey(value.format("DD-MM-YYYY"));
  }

  const str = String(value).trim();
  if (!str) return "";

  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
    const [day, month, year] = str.split("-");
    return `${year}-${month}-${day}`;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    return str;
  }

  const utc = dayjs.utc(str);
  if (!utc.isValid()) return "";

  // Date-only values are stored as midnight UTC (2026-09-17T00:00:00.000Z).
  if (utc.hour() === 0 && utc.minute() === 0 && utc.second() === 0) {
    return utc.format("YYYY-MM-DD");
  }

  // Local-midnight dates may be stored as previous-day UTC evening.
  return dayjs(str).format("YYYY-MM-DD");
};

export const toPickerDate = (value) => {
  const key = toDateKey(value);
  if (!key) return "";
  const [year, month, day] = key.split("-");
  return `${day}-${month}-${year}`;
};

export const returnDate = (value) => {
  const key = toDateKey(value);
  if (!key) return "";
  return new Date(`${key}T00:00:00`).toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const formattedDates = (inputDates = []) => {
  const dates = Array.isArray(inputDates) ? inputDates : [inputDates];
  return dates.map((date) => toDateKey(date)).filter(Boolean);
};

export const displayFormattedDateTime = (dateString, currencySymbol, options = {}) => {
  const { wallClock = false } = options;
  const date = new Date(dateString);
  if (isNaN(date)) return "Invalid Date";

  const locale = currencySymbol === "₹" ? "en-IN" : "en-US";
  // Appointment/follow-up date_time is stored as UTC with wall-clock components (no TZ shift).
  // created_date and other real timestamps use the user's region timezone.
  const timeZone = wallClock
    ? "UTC"
    : currencySymbol === "₹"
      ? "Asia/Kolkata"
      : "UTC";

  return date.toLocaleString(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone,
  });
};


export const displayFormattedDate = (dateString,currencySymbol ) => {
  const date = new Date(dateString);
  if (isNaN(date)) return "Invalid Date";

  
  const locale = currencySymbol === "₹" ? "en-IN" : "en-US";
  const timeZone = currencySymbol === "₹" ? "Asia/Kolkata" : "UTC";


  return date.toLocaleString( locale , {
    day: "2-digit",
    month: "short",
    year: "numeric",
    // hour: "2-digit",
    // minute: "2-digit",
    // // second: "2-digit",
    // hour12: true,
    timeZone, // <-- control timezone explicitly
  });
};

export const addIdsTolist = (lists) => {
  return lists.map((list, index) => ({
    id: index + 1, // or use a UUID if needed
    ...list,
  }));
};

/** HH:mm for time inputs from API datetime (wall-clock in UTC storage). */
export const dateToTimeString = (val) => {
  if (!val) return "";
  if (!String(val).includes("T")) {
    return String(val).slice(0, 5);
  }
  const d = dayjs.utc(val);
  if (!d.isValid()) {
    const [, timePart = ""] = String(val).split("T");
    return timePart.slice(0, 5);
  }
  return d.format("HH:mm");
};

export const normalizeBookingForArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value != null && value !== "") return [String(value)];
  return [];
};

/** Ensures sub_venue.event_booking_for matches event booking_for for API submit. */
export const syncSubVenueWithBookingFor = (eventDetails) => {
  const bookingFor = normalizeBookingForArray(eventDetails?.booking_for);
  const rawSubVenue = eventDetails?.sub_venue;
  const subVenue =
    rawSubVenue && typeof rawSubVenue === "object"
      ? { ...rawSubVenue }
      : {
          sub_venue_id: eventDetails?.sub_venue_id ?? "",
          sub_venue_name: typeof rawSubVenue === "string" ? rawSubVenue : "",
        };

  return {
    ...eventDetails,
    sub_venue: {
      ...subVenue,
      sub_venue_id: subVenue.sub_venue_id ?? eventDetails?.sub_venue_id ?? "",
      event_booking_for: bookingFor,
    },
  };
};

 export const updateColorBasedOnStatus =(status ) =>{
  let color ="";
    switch(status.toLowerCase()){
      case 'in progress'      :
      case 'lead'           :   color = VM_Colors.lead;  break;

      case 'completed'      :
      case 'confirmed'      :   color = VM_Colors.success;  break;  

      case 'incompleted'    :
      case 'pending'        :  
      case 'not-interested' :   color = VM_Colors.error;  break;  

      default               :   color = VM_Colors.primary; break;
    }
  return color; 
 }

 
function pad(num) {
  return num.toString().padStart(2, '0');
}

export function isTimeRangeWithin12Hours(checkIn, checkOut,timeFormat) {
  if(timeFormat.includes("24")){
      if (!checkIn || !checkOut) return false;

        const [inH, inM] = checkIn.split(':').map(Number);
        const [outH, outM] = checkOut.split(':').map(Number);

        // Always start with a fresh "today" date
        const now = new Date();

        const checkInDate = new Date(now); // fresh copy of current date
        checkInDate.setHours(inH, inM, 0, 0);

        const checkOutDate = new Date(now); // another fresh copy
        checkOutDate.setHours(outH, outM, 0, 0);

        // If checkout is before or equal to checkin, assume next day
        if (checkOutDate <= checkInDate) {
          checkOutDate.setDate(checkOutDate.getDate() + 1);
        }

        const diffMs = checkOutDate - checkInDate;
        const diffHours = diffMs / (1000 * 60 * 60);

        console.log("24Hr: ", checkInDate + " - " + checkOutDate + " = " + diffHours);

        return diffHours <= 24;
  }else{
     if (!checkIn || !checkOut) return false;

      const [inH, inM] = checkIn.split(':').map(Number);
      const [outH, outM] = checkOut.split(':').map(Number);

     
  const today = new Date().toISOString().split('T')[0]; // yyyy-mm-dd

  const checkInDate = new Date(`${today}T${pad(inH)}:${pad(inM)}:${pad(0)}`);
  let checkOutDate = new Date(`${today}T${pad(outH)}:${pad(outM)}:${pad(0)}`);

      
      // If checkout is before or equal to checkin, assume next day
      if (checkOutDate <= checkInDate) {
        checkOutDate.setDate(checkOutDate.getDate() + 1);
      }
      const diffMs = checkOutDate - checkInDate;
      const diffHours = diffMs / (1000 * 60 * 60);
      console.log("12Hr: ",checkInDate+" - "+checkOutDate +" = "+diffHours)
      return diffHours <= 12;
  }
}

export const updateDDMMYYYTimeToYYMMDDTimeAdd1DayExtra = (input) => {
  // Split date and time
const [datePart, timePart] = input.split(" ");
const [day, month, year] = datePart.split("-").map(Number);

// Create Date object (month is 0-based in JS)
const date = new Date(year, month - 1, day);

// Add 1 day
date.setDate(date.getDate() + 1);

// Format back to YYYY-MM-DD HH:mm:ss+00
const pad = (n) => n.toString().padStart(2, '0');
const formatted = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${timePart}+00`;

return formatted; // Output: 2025-06-27 15:00:00+00
}


export const updateDDMMYYYTimeToYYMMDDTime = (input) => {
      // Split date and time
    const [datePart, timePart] = input.split(" ");
    const [day, month, year] = datePart.split("-").map(Number);

    // Create Date object (month is 0-based in JS)
    const date = new Date(year, month - 1, day);


    // Format back to YYYY-MM-DD HH:mm:ss+00
    const pad = (n) => n.toString().padStart(2, '0');
    const formatted = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${timePart}+00`;

return formatted; 
}


export const formattedDateTime = (date) => {
  return dayjs(date).format('DD-MMM-YYYY hh:mm A')

}

/** ISO datetime for API — wall-clock components only (no timezone shift). */
export const formatWallClockIsoForApi = (value) => {
  if (!value) return "";
  const d = dayjs.isDayjs(value) ? value : dayjs(value);
  const local = d.$u ? d.local() : d;
  return local.format("YYYY-MM-DDTHH:mm:ss");
};

/** Display API datetime stored as UTC with wall-clock components. */
export const formattedWallClockDateTime = (value) => {
  if (!value) return "";
  const d = dayjs.utc(value);
  if (!d.isValid()) {
    return dayjs(value).format("DD-MMM-YYYY hh:mm A");
  }
  return d.format("DD-MMM-YYYY hh:mm A");
};

/** Wall-clock time from DateTimePicker for API payloads (matches what the user selected). */
export const formatDateTimePickerForApi = (value) => {
  if (!value) return '';
  const d = dayjs.isDayjs(value) ? value : dayjs(value);
  const local = d.$u ? d.local() : d;
  return local.format('DD-MMM-YYYY hh:mm A');
};

/** Parse API datetime for DateTimePicker (wall-clock from UTC-stored value). */
export const parseDateTimeFromApiForPicker = (value) => {
  if (!value) return null;
  const d = dayjs.utc(value);
  if (!d.isValid()) return null;
  return dayjs()
    .year(d.year())
    .month(d.month())
    .date(d.date())
    .hour(d.hour())
    .minute(d.minute())
    .second(0)
    .millisecond(0);
};

export function YYYYMMDDToDDMMYYYY (dateStr) {
  if (!dateStr) return "";

  const [year, month, day] = dateStr.split("-");
  return `${day}-${month}-${year}`;
}

export function formatDateYYYYMMDD(d) {
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export const formatDateOnlyFromTDate = (isoString) => {
 
  if (!isoString) return "-";
  
  const [datePart] = isoString.split("T");   // "2026-05-03"
  const [year, month, day] = datePart.split("-");

  return `${day}-${month}-${year}`;
};

export function getStartOfCurrentMonth() {
  const now = new Date();
  return formatDateYYYYMMDD(new Date(now.getFullYear(), now.getMonth(), 1));
}

export function getEndOfCurrentMonth() {
  const now = new Date();
  // last day: create date for next month, day 0
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return formatDateYYYYMMDD(lastDay);
}

export const invoice_template_object = (enquiry_id,user_id) => {
  return  [
    {
      id: 'template1',
      title: 'Template 1',
      image: `/static/invoice_template1.svg`,
      previewUrl: API_DISPLAY_INVOICE+'?enquiry_id='+enquiry_id+'&user_id='+user_id+'&template_id=1',
    },
    {
      id: 'template2',
      title: 'Template 2',
      image: `/static/invoice_template2.svg`,
      previewUrl: API_DISPLAY_INVOICE+'?enquiry_id='+enquiry_id+'&user_id='+user_id+'&template_id=2',
    },
    {
      id: 'template3',
      title: 'Template 3',
      image: `/static/invoice_template3.svg`,
      previewUrl: API_DISPLAY_INVOICE+'?enquiry_id='+enquiry_id+'&user_id='+user_id+'&template_id=3',
    },
  ]
}


export const SummaryLine = ({ label, value, currencySymbol }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) auto auto',
      alignItems: 'center',
      columnGap: 1,
      py: 1,
      width: '100%',
      minWidth: 0,
      boxSizing: 'border-box',
    }}
  >
    <MUITypography
      variant="body1"
      sx={{
        color: '#444',
        fontWeight: 500,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </MUITypography>
    {/* <MUITypography variant="body1" sx={{ color: '#888', flexShrink: 0 }}>
      :
    </MUITypography> */}
    <MUITypography
      variant="body1"
      sx={{
        fontWeight: 600,
        color: '#111',
        textAlign: 'right',
        whiteSpace: 'nowrap',
        minWidth: 0,
        justifySelf: 'end',
      }}
    >
      {formatAmount(Number(value), currencySymbol)}
    </MUITypography>
  </Box>
);

export const isAdminLoggedIn = (userType) => {
  return typeof userType === "string" && userType.toLowerCase().trim() === "admin";
};

export const checkIsPermissionExist = (VM_PERMISSIONS,permissionID) => {
  const sessionFactory = new StorageFactory('session');
  const userType = sessionFactory.get(SESSION_USER_TYPE_KEY);
  // if(isEmpty(VM_PERMISSIONS) || isAdminLoggedIn(userType) )
  //    return true;
  if( (!isEmpty(VM_PERMISSIONS) && VM_PERMISSIONS.includes(permissionID) ) || isAdminLoggedIn(userType) ){
 
  
  // const updatedPermissions = VM_PERMISSIONS.map(perm => perm.permission_id);
  // if(isEmpty(updatedPermissions) || isAdminLoggedIn(userType) )
  //    return true;
  // if(updatedPermissions.includes(permissionID) || isAdminLoggedIn(userType) ){
    return true;
  }else{
   return false;
  }
}

export const vendorTypes = ["Event Manager", "Catering","Tent House","Photographer","Sound and Lighting", "Others"];


export function isUserSubscribed( is_paid , is_subscription_active) {
  const isPaid = Boolean(is_paid);
  const isSubActive = Boolean(is_subscription_active);

  if (isPaid & isSubActive) return true;
  if (!isPaid && isSubActive) return true;
  return false;
}
export const formatAmount = (amount, currencySymbol) => {
  const symbol = currencySymbol || "₹";
  if (isEmpty(amount) || isNaN(Number(amount))) {
    return `${symbol}0.00`;
  }

  const locale = symbol === "₹" ? "en-IN" : "en-US";
  try {
    return `${symbol}${Number(amount).toLocaleString(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  } catch {
    return `${symbol}${Number(amount).toFixed(2)}`;
  }
};


export const renderSNo = () => {
  return  {
    field: 's_no',
    headerName: 'S.No',
    flex: 0,
    minWidth: 48,
    maxWidth: 64,
    sortable: false,
    disableColumnMenu: false,
    headerAlign: "left",
    align: "left",
    renderCell: (params) => {
      const index = params.row.s_no
      return (
        <Box
          sx={{
            height: "100%",              // 🔑 take full cell height
            width: "100%",               // 🔑 take full cell width
            display: "flex",
            alignItems: "center",        // 🔑 vertical center
            justifyContent: "flex-start",
          }}
        >
          <Box
            sx={{
              width: { xs: 26, sm: 28 },
              height: { xs: 26, sm: 28 },
              borderRadius: "50%",
              backgroundColor: VM_Colors.shadow,
              color: VM_Colors.black,
              fontSize: { xs: 12, sm: 13 },
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {index}
          </Box>
        </Box>
      );
    },
  } 
}

export const isTodayDate = (date) => {
  const d = new Date(date);
  const today = new Date();

  return (
    d.getDate() === today.getDate() &&
    d.getMonth() === today.getMonth() &&
    d.getFullYear() === today.getFullYear()
  );
}

export function toTitleCase(str = "") {
  return str
    .toLowerCase()
    .trim()
    .split(/\s+/) // handles multiple spaces
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/** Title-case each word while typing. Trailing spaces are kept so the next word can be entered. */
export function toTitleCaseInput(str = "") {
  return String(str).replace(/\S+/g, (word) =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  );
}


export const handleWheel = (maybeTypeOrEvent) => {
  // Supports two usages:
  // 1) as a handler: onWheel={handleWheel} => handleWheel(event)
  // 2) as a factory: onWheel={handleWheel('number')} => returns (event) => {...}
  if (typeof maybeTypeOrEvent === 'string') {
    const type = maybeTypeOrEvent;
    return (e) => {
      if (type === 'number') {
        e.preventDefault();
        e.stopPropagation();
        try { e.target && e.target.blur && e.target.blur(); } catch (err) {}
      }
    };
  }

  // Called directly as handler
  const e = maybeTypeOrEvent;
  const t = e?.target?.type;
  const inputMode = e?.target?.getAttribute && e.target.getAttribute('inputmode');
  const isNumeric = t === 'number' || inputMode === 'numeric';
  if (isNumeric) {
    e.preventDefault();
    e.stopPropagation();
    try { e.target && e.target.blur && e.target.blur(); } catch (err) {}
  }
};

export const parsePct = (v) => {
    if (v === '' || v === null || v === undefined) return NaN;
    const n = typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'));
    return Number.isFinite(n) ? n : NaN;
  };

  export const renderWithTooltip = (value) => (
      <Tooltip title={value || ''}>
        <span>{value}</span>
      </Tooltip>
    );

  export const checkIsMenuPermissionExist = (permissions = [], permission) => {
  if (!permission) return true;
  if (!Array.isArray(permissions)) return false;
  return permissions.includes(permission);
};

export const UNBOOKED_ACCOMMODATION_ALERT_MESSAGE =
'You have not booked any rooms for this enquiry. Please book rooms before proceeding with invoice creation.';

export const fetchAdminTeamMembers = async (userId) => {
  const res = await axios.get(`${API_GET_ADMIN_MEMEBRS}?user_id=${userId}`);
  const members = res.data?.data;
  if (isEmpty(members)) return [];
  return Array.isArray(members) ? members : [];
};

export const hasAdminTeamMembers = async (userId) => {
  try {
    const members = await fetchAdminTeamMembers(userId);
    return members.length > 0;
  } catch {
    return false;
  }
};